import { DecimalPipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WallSide } from '../enums/wallSide.enum';
import { CollisionPoint } from '../models/collisionPoint.model';
import { Conflicts } from '../models/conflicts.model';
import { InnerWall } from '../models/innerWall.model';
import { LanguageService } from '../services/language.service';
import { ThreeDService } from '../services/three-d.service';

@Component({
  selector: 'app-conflicts-panel',
  templateUrl: './conflicts-panel.component.html',
  styleUrls: ['./conflicts-panel.component.scss'],
})
export class ConflictsPanelComponent implements OnInit {
  conflicts: Conflicts = {} as Conflicts;
  decimalPipe: DecimalPipe;

  constructor(
    private threeDService: ThreeDService,
    private cd: ChangeDetectorRef,
    private translateService: TranslateService,
    private languageService: LanguageService
  ) {
    this.decimalPipe = new DecimalPipe(this.currentLocale);
  }
  get currentLocale() {
    return this.languageService.currentLanguage;
  }
  get hasAny() {
    return this.conflicts.collisions.length > 0 || this.conflicts.invalidPanels.length > 0;
  }
  ngOnInit(): void {
    this.threeDService.conflicts$.subscribe(conflicts => {
      this.conflicts = conflicts;
      this.cd.detectChanges();
    });
  }

  get totalInstanceCount() {
    return this.conflicts.collisions.length + this.conflicts.invalidPanels.reduce((a, b) => (a += b.count), 0);
  }

  getTitleForCollision(collisionPoint: CollisionPoint): string {
    return (
      this.getADescriptionForCollision(collisionPoint) +
      ' ' +
      this.translateService.instant('AND') +
      ' ' +
      this.getBDescriptionForCollision(collisionPoint)
    );
  }

  getADescriptionForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshAIsDoor) {
      text +=
        this.translateService.instant('DOOR') +
        ' ' +
        this.translateService.instant('WALL.' + collisionPoint.doorA.side);
      if (collisionPoint.doorA.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.doorA.innerWallId)}`;
      }
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallA.side);
      if (collisionPoint.wallA.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallA.innerWall.id)}`;
      }
    }
    return text;
  }
  getBDescriptionForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshBIsDoor) {
      text +=
        this.translateService.instant('DOOR') +
        ' ' +
        this.translateService.instant('WALL.' + collisionPoint.doorB.side);
      if (collisionPoint.doorB.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.doorB.innerWallId)}`;
      }
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallB.side);
      if (collisionPoint.wallB.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallB.innerWall.id)}\n`;
        text += `${this.translateService.instant('WALL.' + collisionPoint.wallB.innerWall.placedOnSide)}`;
      }
    }
    return text;
  }

  getATextForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshAIsDoor) {
      text += this.translateService.instant('DOOR');
      text += ` ${this.numberToString(collisionPoint.doorA.left)} mm ${this.translateService.instant(
        'SUMMARY.FROMLEFTCORNER'
      )}`;
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallA.side);
      if (collisionPoint.wallA.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallA.innerWall.id)}`;
        text += '\n';
        text += `${this.translateService.instant(
          'WALL.' + collisionPoint.wallA.innerWall.placedOnSide
        )} ${this.numberToString(collisionPoint.wallA.innerWall.left)} mm ${this.translateService.instant(
          'SUMMARY.FROMLEFTCORNER'
        )}`;
      }
    }
    return text;
  }
  getBTextForCollision(collisionPoint: CollisionPoint): string {
    let text = '';
    if (collisionPoint.meshBIsDoor) {
      text += this.translateService.instant('DOOR');
      text += ` ${this.numberToString(collisionPoint.doorB.left)} mm ${this.translateService.instant(
        'SUMMARY.FROMLEFTCORNER'
      )}`;
    } else {
      text += this.translateService.instant('WALL.' + collisionPoint.wallB.side);
      if (collisionPoint.wallB.side === WallSide.inner) {
        text += ` ${this.getId(collisionPoint.wallB.innerWall.id)}\n`;
        text += `${this.translateService.instant(
          'WALL.' + collisionPoint.wallB.innerWall.placedOnSide
        )} ${this.numberToString(collisionPoint.wallB.innerWall.left)} mm ${this.translateService.instant(
          'SUMMARY.FROMLEFTCORNER'
        )}`;
      }
    }
    return text;
  }

  numberToString(value: number): string {
    return this.decimalPipe.transform(value, '1.0-0');
  }

  getId(id: string) {
    if (!id) {
      return '';
    }
    const names = id.split('-');
    if (names.length > 1) {
      return +names[1] + 1;
    }
    return '';
  }
  getLeft(iw: InnerWall) {
    return iw.left;
  }
}
